/**
 * 63.14 step 2 (ARCHITECTURE §5.22 "the grid and the generator"; DECISIONS 201: (1) the 2-person cube, (4) "start hand
 * made"): modular buildings as data on the kit's grid — pure, keyed and the same for the sim and the renderer (E5.4's
 * rule for the forest's cells). This step: today's ten team buildings (rendering/buildingModels.ts BUILDING_NAMES, sized
 * by function at BUILDING_PERSON_SCALE.team) re-expressed as module layouts at their present footprints (within a cell),
 * and the base templates (`fob`, `outpost`, `wallLine`, `gunPit`) as hand-made layouts, for the sheet and step 5's plans.
 * `posesOf` turns a layout into module poses in people (its building's frame; the renderer draws them instanced from the
 * kit's prototypes behind `GameConfig.modularBuildings`), while the sim still reads the building's one box (63.9) —
 * nothing here is read by a tick, so the switch is bit-identical (tests/modules.test.ts holds the per-tick checksums
 * equal). Step 3 reads the modules as shapes, walls and doors; step 6 generates settlements from these pieces per seed.
 */
import type { BuildingLayout, LayoutModule, ModuleKind, ModulePose } from '../types/modules';
import type { GameConfig, StructureState } from '../types';
import { MODULE_CELL, MODULE_SLAB, MODULE_SPAN, DAMAGED_MODULE_SHARE } from '../constants/modules';
import { buildingsByFunction, builderOf, isPlanetBuilding } from './buildingScale';

const C = MODULE_CELL;
/** cos and sin of a quarter turn (exact; no trig in the data path). */
const COS = [1, 0, -1, 0] as const, SIN = [0, 1, 0, -1] as const;
const spanOf = (k: ModuleKind): number => MODULE_SPAN[k] ?? 1;
/** The kit's guard tower stands this far above its cell's floor (people; its platform and roof — for extents only). */
export const TOWER_HEIGHT = 5;

/** The switch: buildings drawn as modules from the kit (GameConfig.modularBuildings), where they are sized by function (63.7 b). */
export function modularBuildingsOn(config: Pick<GameConfig, 'clock' | 'scale' | 'personTuning' | 'modularBuildings'>): boolean {
  return !!config.modularBuildings && buildingsByFunction(config);
}

// ---- poses ---------------------------------------------------------------------------------------------------------------------

const poseMemo = new WeakMap<BuildingLayout, readonly ModulePose[]>();
/**
 * Every module of a layout posed in its building's frame (people; the origin at the ground centre, x along the columns,
 * z along the rows, y up): the kit's placing rules (baseKit.ts onEdge / atNode / inCell) — a wall centred on its cell's
 * edge, a spanning kind centred on its run from its first cell, a tile or tower on its cell's centre (furniture offset
 * from it), a post at its node; a floor's modules `floor × MODULE_CELL` up. Memoised per layout object.
 */
export function posesOf(layout: BuildingLayout): readonly ModulePose[] {
  const hit = poseMemo.get(layout);
  if (hit) return hit;
  const x0 = (-layout.cols * C) / 2, z0 = (-layout.rows * C) / 2;
  const out: ModulePose[] = layout.modules.map((m, index) => {
    const turn = m.turn ?? 0, h = spanOf(m.kind) / 2, y = (m.floor ?? 0) * C + (m.lift ?? 0);
    let x: number, z: number;
    switch (m.at) {
      case 'edgeX': x = x0 + (m.i + h) * C; z = z0 + m.j * C; break;
      case 'edgeZ': x = x0 + m.i * C; z = z0 + (m.j + h) * C; break;
      case 'node': x = x0 + m.i * C; z = z0 + m.j * C; break;
      case 'cell': { const on = (h - 0.5) * C; x = x0 + (m.i + 0.5) * C + on * COS[turn] + (m.dx ?? 0); z = z0 + (m.j + 0.5) * C - on * SIN[turn] + (m.dz ?? 0); break; }
    }
    return { kind: m.kind, x, y, z, yaw: turn * (Math.PI / 2), index };
  });
  poseMemo.set(layout, out);
  return out;
}

/** A layout's extent round its origin (people): the half widths across x and z of all it places (wire a cell out counts), and its top. */
export function layoutExtent(layout: BuildingLayout): { halfX: number; halfZ: number; height: number } {
  let minX = 0, maxX = layout.cols, minZ = 0, maxZ = layout.rows, top = layout.floors * C;
  for (const m of layout.modules) {
    const span = spanOf(m.kind), i1 = m.at === 'edgeX' ? m.i + span : m.at === 'cell' ? m.i + 1 : m.i, j1 = m.at === 'edgeZ' ? m.j + span : m.at === 'cell' ? m.j + 1 : m.j;
    minX = Math.min(minX, m.i); maxX = Math.max(maxX, i1); minZ = Math.min(minZ, m.j); maxZ = Math.max(maxZ, j1);
    if (m.kind === 'tower') top = Math.max(top, (m.floor ?? 0) * C + TOWER_HEIGHT);
  }
  const cx = layout.cols / 2, cz = layout.rows / 2;
  return { halfX: Math.max(maxX - cx, cx - minX) * C, halfZ: Math.max(maxZ - cz, cz - minZ) * C, height: top };
}

/**
 * The modules a damaged building shows in their damaged form (presentation; 63.4 step 8's one damaged form, per module
 * here): a keyed share of its walls, parapets and towers — floors, roofs, posts and furniture stay whole. The same set
 * for the same structure seed, whatever the frame.
 */
export function damagedModules(layout: BuildingLayout, seed: number, share = DAMAGED_MODULE_SHARE): ReadonlySet<number> {
  const out = new Set<number>();
  layout.modules.forEach((m, k) => {
    if (m.kind === 'floor' || m.kind === 'roof' || m.kind === 'post' || m.kind === 'postT' || m.kind === 'shelving' || m.kind === 'crate' || m.kind === 'stairs' || m.kind === 'ladder') return;
    if (hash32(seed, k) / 4294967296 < share) out.add(k);
  });
  return out;
}

/** A keyed 32-bit hash of two integers (integer arithmetic only: the same in every engine). */
function hash32(a: number, b: number): number {
  let h = (a ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = (h ^ b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

// ---- the generator's pieces (hand-made layouts composed from them) ------------------------------------------------------------

type Side = 'S' | 'N' | 'W' | 'E';
type Turn = 0 | 1 | 2 | 3;
interface BoxOpts {
  i0?: number; j0?: number; cols: number; rows: number; floor0?: number; floors?: number;
  /** The wall kind on face `side`'s panel `k` (0.. along the face, west to east or south to north) on `floor`; null = open. */
  wall: (side: Side, k: number, floor: number) => ModuleKind | null;
  /** The stairs on `floor` (absolute cell; null = none); the floor above keeps no tile over them. Absent = none. */
  stairs?: (floor: number) => { i: number; j: number; turn?: Turn } | null;
  /** Roof slabs over the top floor: all (default), none, or by cell (absolute). */
  roof?: boolean | ((i: number, j: number) => boolean);
  /** Floor tiles on every floor (default) — the ground floor's too (walkable at step 3). */
  tiles?: boolean;
  /** Corner posts, every floor (default). */
  posts?: boolean;
}

/**
 * A walled box of cells: its four faces' panels per floor (a wall's outside faces out: south 2, north 0, west 3, east 1
 * quarter turns), corner posts, floor tiles, the stairs, roof slabs over its top floor.
 */
function box(out: LayoutModule[], o: BoxOpts): void {
  const i0 = o.i0 ?? 0, j0 = o.j0 ?? 0, f0 = o.floor0 ?? 0, floors = o.floors ?? 1, last = f0 + floors - 1;
  for (let f = f0; f <= last; f++) {
    for (let k = 0; k < o.cols; k++) {
      const s = o.wall('S', k, f), n = o.wall('N', k, f);
      if (s) out.push({ kind: s, at: 'edgeX', i: i0 + k, j: j0, floor: f, turn: 2 });
      if (n) out.push({ kind: n, at: 'edgeX', i: i0 + k, j: j0 + o.rows, floor: f, turn: 0 });
    }
    for (let k = 0; k < o.rows; k++) {
      const w = o.wall('W', k, f), e = o.wall('E', k, f);
      if (w) out.push({ kind: w, at: 'edgeZ', i: i0, j: j0 + k, floor: f, turn: 3 });
      if (e) out.push({ kind: e, at: 'edgeZ', i: i0 + o.cols, j: j0 + k, floor: f, turn: 1 });
    }
    if (o.posts !== false) for (const [i, j] of [[i0, j0], [i0 + o.cols, j0], [i0, j0 + o.rows], [i0 + o.cols, j0 + o.rows]]) out.push({ kind: 'post', at: 'node', i, j, floor: f });
    const below = f > 0 ? o.stairs?.(f - 1) ?? null : null;
    if (o.tiles !== false) for (let i = 0; i < o.cols; i++) for (let j = 0; j < o.rows; j++) if (!below || below.i !== i0 + i || below.j !== j0 + j) out.push({ kind: 'floor', at: 'cell', i: i0 + i, j: j0 + j, floor: f });
    const st = o.stairs?.(f);
    if (st) out.push({ kind: 'stairs', at: 'cell', i: st.i, j: st.j, floor: f, turn: st.turn ?? 0 });
  }
  const roof = o.roof ?? true;
  if (roof) for (let i = 0; i < o.cols; i++) for (let j = 0; j < o.rows; j++) if (roof === true || roof(i0 + i, j0 + j)) out.push({ kind: 'roof', at: 'cell', i: i0 + i, j: j0 + j, floor: last });
}

/** Four storage crates in a cell (the kit's crate cell: the storage unit), on the tile. */
function crates(out: LayoutModule[], i: number, j: number, floor = 0): void {
  for (const [dx, dz] of [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0.5], [0.5, 0.5]]) out.push({ kind: 'crate', at: 'cell', i, j, floor, dx, dz, lift: MODULE_SLAB });
}
/** Shelving against a cell's wall: `turn` the way it faces (0 north with its back to the south wall, 2 south, 1 east, 3 west). */
function shelves(out: LayoutModule[], i: number, j: number, turn: Turn, floor = 0): void {
  const back = 0.67;
  out.push({ kind: 'shelving', at: 'cell', i, j, floor, turn, dx: turn === 1 ? -back : turn === 3 ? back : 0, dz: turn === 0 ? -back : turn === 2 ? back : 0, lift: MODULE_SLAB });
}
/** A straight run of a wall kind along +x from node (i, j) over `n` cells, `turn` its outside; `skip` leaves cells open. */
function run(out: LayoutModule[], kind: ModuleKind, i: number, j: number, n: number, turn: Turn, along: 'x' | 'z', skip: (k: number) => boolean = () => false): void {
  for (let k = 0; k < n; k++) if (!skip(k)) out.push(along === 'x' ? { kind, at: 'edgeX', i: i + k, j, turn } : { kind, at: 'edgeZ', i, j: j + k, turn });
}
/**
 * 67.65's corners: a sandbag run stops half a corner short of each node, so every node a run joins — a corner, a joint in
 * line, an end against a Hesco or a T-wall — takes a `sandbagCorner` pier; a free end none. `nodes`: the (i, j) nodes.
 */
function sandbagCorners(out: LayoutModule[], nodes: ReadonlyArray<readonly [number, number]>): void {
  for (const [i, j] of nodes) out.push({ kind: 'sandbagCorner', at: 'node', i, j });
}
/**
 * A guard tower in a cell, its stairs in the cell south of it (the climb into it) — or north of it, the tower turned to meet
 * them (63.16 (4): a stairs' foot must open onto the yard, not onto a wall; the FOB's tower stands a cell in from the south Hesco).
 */
function tower(out: LayoutModule[], i: number, j: number, from: 'south' | 'north' = 'south'): void {
  out.push({ kind: 'tower', at: 'cell', i, j, ...(from === 'north' ? { turn: 2 } : {}) });
  out.push({ kind: 'stairs', at: 'cell', i, j: from === 'north' ? j + 1 : j - 1, turn: from === 'north' ? 2 : 0 });
}
const make = (key: string, cols: number, rows: number, floors: number, build: (out: LayoutModule[]) => void): BuildingLayout => {
  const out: LayoutModule[] = [];
  build(out);
  return { key, cols, rows, floors, modules: out };
};
/** A face's panels: a door at `door` on the ground floor, else a window on every other panel from the second. */
const doorAndWindows = (door: Partial<Record<Side, number>>, upper: ModuleKind = 'wallWindow') => (s: Side, k: number, f: number): ModuleKind =>
  f === 0 ? (door[s] === k ? 'wallDoor' : k % 2 === 1 ? 'wallWindow' : 'wallPlain') : k % 2 === 1 ? upper : 'wallPlain';

// ---- 67.75 / 69.14: the motor pool's plan parts (core/motorPool.ts MOTOR_POOL reads them) and its layout as the sim stands it -------

const rowsOfSlots = (j: number) => [1, 4, 10, 13].map(i => ({ i, j, tiers: 3 }));
/**
 * The motor pool's plan inside its walls (core/motorPool.ts MOTOR_POOL, which documents it): four rows of slots, four stacks
 * each either side of the passage (a slot three cells along x from (i, j), three frames high at most); the lanes; the stacker
 * arm's station; the corner robots' two bays; the unpack pad.
 */
export const MOTOR_POOL_PARTS = {
  slots: [...rowsOfSlots(4), ...rowsOfSlots(8), ...rowsOfSlots(9), ...rowsOfSlots(13)],
  lanes: [{ i: 1, j: 5, cols: 15, rows: 3 }, { i: 1, j: 10, cols: 15, rows: 3 }, { i: 7, j: 1, cols: 3, rows: 14 }],
  arm: { i: 11, j: 6, turn: 0 as const },
  bays: [{ i: 10, j: 2 }, { i: 13, j: 2 }],
  pad: { i: 2, j: 2, alongX: true as const },   // (69.14: along x, its ramp toward the passage — it faced the front wall)
};
const poolLayouts = new Map<string, BuildingLayout>();
/**
 * 69.14 (the owner: "everything real and collidable in motor pool"): the pool's compound as the sim stands it — its walls
 * (BASE_TEMPLATES.motorPool, their indices first and unchanged), then the stacker arm parked at its station, the corner robots
 * parked in each bay, and a stack module on the middle cell of every slot `stacks` fills (1–3 tiers: the frames' boxes as the
 * look stands them, rendering/motorPoolPlay.ts stackPose). Shapes only: the kit leaves them to the pool's look
 * (constants/modules.ts DRAWN_BY_THE_POOL). One layout object a stacks pattern (memoised: the shapes' caches key on it).
 */
export function motorPoolLayout(stacks: readonly number[] | undefined): BuildingLayout {
  const key = stacks?.join(',') ?? '';
  const hit = poolLayouts.get(key);
  if (hit) return hit;
  const walls = BASE_TEMPLATES.motorPool, P = MOTOR_POOL_PARTS, extra: LayoutModule[] = [];
  extra.push({ kind: 'stackerArm', at: 'cell', i: P.arm.i, j: P.arm.j, turn: P.arm.turn });
  for (const b of P.bays) extra.push({ kind: 'cornerRobots', at: 'cell', i: b.i + 1, j: b.j });
  P.slots.forEach((sl, k) => {
    const t = Math.min(3, Math.max(0, Math.floor(stacks?.[k] ?? 0)));
    if (t > 0) extra.push({ kind: (['frameStack1', 'frameStack2', 'frameStack3'] as const)[t - 1], at: 'cell', i: sl.i + 1, j: sl.j });
  });
  const layout: BuildingLayout = { ...walls, modules: [...walls.modules, ...extra] };
  if (poolLayouts.size > 256) poolLayouts.clear();
  poolLayouts.set(key, layout);
  return layout;
}

// ---- today's ten team buildings as layouts (BUILDING_PERSON_SCALE.team's order; footprints within a cell of theirs) ---------------

/**
 * The ten builders' buildings (rendering/buildingModels.ts BUILDING_NAMES, in BUILDING_PERSON_SCALE.team's order: the
 * team's builder is its index mod ten, core/buildingScale.ts builderOf), each a layout at its present footprint —
 * `size` (model units, REFERENCE.person.height = 1.8 a person: the hitboxes agree, 5–12 people across) over the 2-person cell,
 * rounded, so the board keeps its spacing with the switch on. Hand made (201 (4)).
 */
export const TEAM_LAYOUTS: readonly BuildingLayout[] = [
  // 0 Survey Outpost 10 × 10 × 5.7 people: a dome of two floors on its plinth — 5 × 5 cells, two floors, glazed above
  make('surveyOutpost', 5, 5, 2, out => {
    box(out, { cols: 5, rows: 5, floors: 2, wall: (s, k, f) => f === 0 ? doorAndWindows({ S: 2, N: 2 })(s, k, f) : 'wallWindow', stairs: f => f === 0 ? { i: 0, j: 0, turn: 2 } : null });   // (63.16 (4): the corner stairs turned to climb south, their foot open to the room — at turn 0 it was on the south wall's line: "stairs ... are against the wall")
    shelves(out, 4, 4, 2);
  }),
  // 1 Greenhouse Pod 9.4 × 7.9 × 3.3: a polytunnel — 5 × 4 cells, one floor of glazing
  make('greenhousePod', 5, 4, 1, out => box(out, { cols: 5, rows: 4, wall: (s, k) => s === 'S' && k === 2 ? 'wallDoor' : 'wallWindow' })),
  // 2 Visitor Experience Pavilion 6.7 × 5.8 × 4.6: two floors under a canopy — 3 × 3 cells, the upper floor glazed round
  make('visitorPavilion', 3, 3, 2, out => box(out, { cols: 3, rows: 3, floors: 2, wall: (s, k, f) => f === 0 ? doorAndWindows({ S: 1, E: 1 })(s, k, f) : 'wallWindow', stairs: f => f === 0 ? { i: 0, j: 2 } : null })),
  // 3 Field Office Module 10.3 × 7.1 × 5.9: a two-floor module — 5 × 4 cells
  make('fieldOffice', 5, 4, 2, out => {
    box(out, { cols: 5, rows: 4, floors: 2, wall: doorAndWindows({ S: 2, N: 2 }), stairs: f => f === 0 ? { i: 0, j: 0, turn: 2 } : null });
    shelves(out, 4, 3, 2); shelves(out, 4, 3, 2, 1);
  }),
  // 4 Supply Depot 5.3 × 3.4 × 2.2: a container with a drone pad on its roof — 3 × 2 cells, one floor, a store
  make('supplyDepot', 3, 2, 1, out => {
    box(out, { cols: 3, rows: 2, wall: (s, k) => s === 'S' && k === 1 ? 'wallDoor' : 'wallPlain' });
    crates(out, 0, 0); crates(out, 0, 1); crates(out, 2, 0); crates(out, 2, 1); shelves(out, 1, 1, 2);
  }),
  // 5 Site Trailer 5.2 × 3.2 × 2.1: a trailer — 3 × 2 cells, one floor
  make('siteTrailer', 3, 2, 1, out => box(out, { cols: 3, rows: 2, wall: (s, k) => s === 'S' && k === 0 ? 'wallDoor' : (s === 'S' || s === 'N') && k === 1 ? 'wallWindow' : s === 'E' ? 'wallWindow' : 'wallPlain' })),
  // 6 Analytics Station 11.2 × 10 × 9.4: two floors and an observation floor — 6 × 5 cells, a 2 × 3 glazed floor on top
  make('analyticsStation', 6, 5, 3, out => {
    box(out, { cols: 6, rows: 5, floors: 2, wall: doorAndWindows({ S: 3 }), stairs: f => f === 0 ? { i: 0, j: 0, turn: 2 } : f === 1 ? { i: 2, j: 1 } : null, roof: (i, j) => !(i >= 2 && i < 4 && j >= 1 && j < 4) });
    box(out, { i0: 2, j0: 1, cols: 2, rows: 3, floor0: 2, wall: () => 'wallWindow', stairs: f => f === 1 ? { i: 2, j: 1 } : null });
  }),
  // 7 Standard Outpost 8.1 × 8.1 × 5.8: two floors, regulation — 4 × 4 cells, slits at the upper corners
  make('standardOutpost', 4, 4, 2, out => box(out, { cols: 4, rows: 4, floors: 2, wall: (s, k, f) => f === 1 && (k === 0 || k === 3) ? 'wallSlit' : doorAndWindows({ S: 1 })(s, k, f), stairs: f => f === 0 ? { i: 0, j: 3 } : null })),
  // 8 Salvage Station 6.7 × 4.2 × 3.3: a hull section on struts — 3 × 2 cells, one floor, its ports slits
  make('salvageStation', 3, 2, 1, out => {
    box(out, { cols: 3, rows: 2, wall: (s, k) => s === 'S' && k === 1 ? 'wallDoor' : (s === 'S' || s === 'N') && k !== 1 ? 'wallSlit' : 'wallPlain' });
    crates(out, 2, 1); shelves(out, 0, 1, 2);
  }),
  // 9 Watchtower 5.3 × 5.1 × 7.9: a base floor and a two-floor tower — a 3 × 3 base, a 2 × 2 tower of two floors on it
  make('watchtower', 3, 3, 3, out => {
    box(out, { cols: 3, rows: 3, wall: doorAndWindows({ S: 1 }), stairs: f => f === 0 ? { i: 1, j: 1 } : null, roof: (i, j) => !(i >= 1 && i < 3 && j >= 1 && j < 3) });
    box(out, { i0: 1, j0: 1, cols: 2, rows: 2, floor0: 1, floors: 2, wall: (s, k, f) => f === 2 ? (k === 0 ? 'wallSlit' : 'wallWindow') : k === 0 ? 'wallWindow' : 'wallPlain', stairs: f => f <= 1 ? { i: 1, j: 1, turn: 2 } : null });
  }),
];

/** The layout a team's builder puts up (team index mod ten, as core/buildingScale.ts builderOf). */
export function teamLayout(builder: number): BuildingLayout {
  return TEAM_LAYOUTS[builderOf(builder)];
}

/**
 * The layout a structure is drawn from under the switch: a planet-scale building sized by function (the team's building:
 * its builder's layout; the antenna is its post, no layout) where modular buildings are on; else null (the sim's box and
 * today's models in every other case).
 */
export function layoutFor(s: Pick<StructureState, 'type' | 'builder'> & Partial<Pick<StructureState, 'stacks'>>, config: Pick<GameConfig, 'clock' | 'scale' | 'personTuning' | 'modularBuildings'> | undefined): BuildingLayout | null {
  if (s.type === 'motorPool') return motorPoolLayout(s.stacks);   // 63.16 (7): the pool's compound stands on its own switch (GameConfig.motorPool), whatever the modular one says; 69.14: with its stacks, its arm and its robots
  if (!config || !modularBuildingsOn(config)) return null;
  return layoutOfStructure(s);
}

/** The layout a structure would take under the switch (its builder's; none for the antenna's post and 67.62's D structures, the HQ and the pad, which have their own shapes), config aside. */
export function layoutOfStructure(s: Pick<StructureState, 'type' | 'builder'>): BuildingLayout | null {
  if (s.type === 'motorPool') return BASE_TEMPLATES.motorPool;   // (63.16 (7))
  if (!isPlanetBuilding(s.type) || s.type === 'antenna' || s.type === 'ecotourism' || s.type === 'landingPad') return null;
  return teamLayout(s.builder ?? 0);
}

// ---- the base templates (hand made; drawn on the sheet, step 5's plans build them) ---------------------------------------------

export type BaseTemplate = 'fob' | 'outpost' | 'wallLine' | 'gunPit' | 'motorPool';
/**
 * The bases a side builds (§5.22 "bases built from modules"; 69.4's FOB becomes the `fob` template at step 5): a Hesco
 * compound with a vehicle gate, stores and a guard tower (`fob`); a T-walled compound with a hut, a container room, a
 * parapet and a tower (`outpost`); a T-wall run with a gate and wire before it (`wallLine`); a sandbag ring open to the
 * rear, the HMG's (`gunPit`); 67.75's motor pool's perimeter, a Hesco square with a wide gate south and wire before it
 * (`motorPool`; its slots, lanes and stations are core/motorPool.ts MOTOR_POOL, drawn only; 69.10 a back gate north onto the
 * lighters' strip). Cells are the grid's; wire
 * stands a cell outside the wall (j −1).
 */
export const BASE_TEMPLATES: Readonly<Record<BaseTemplate, BuildingLayout>> = {
  fob: make('fob', 6, 6, 1, out => {
    run(out, 'hesco', 0, 0, 6, 2, 'x', k => k === 2 || k === 3);
    out.push({ kind: 'gateWide', at: 'edgeX', i: 2, j: 0, turn: 2 });
    run(out, 'hesco', 0, 6, 6, 0, 'x');
    run(out, 'hesco', 0, 0, 6, 3, 'z');
    run(out, 'hesco', 6, 0, 6, 1, 'z');
    run(out, 'wire', 0, -1, 6, 2, 'x', k => k === 2 || k === 3);
    out.push({ kind: 'containerRoom', at: 'cell', i: 1, j: 4, turn: 0 });   // (cells (1, 4)–(2, 4), a cell in from the west Hesco, its doors south into the yard)
    crates(out, 3, 4); crates(out, 4, 4); crates(out, 4, 3);   // (a cell in from the east Hesco: a crate at the yard's edge stood inside the bastion — orbital-design-3's z-fight detector, 67.65)
    tower(out, 1, 1, 'north');   // (a cell in from the west Hesco; its stairs at (1, 2), climbing south into it — 63.16 (4): at (1, 0) their foot was on the south Hesco)
  }),
  outpost: make('outpost', 10, 8, 1, out => {
    run(out, 'tWall', 0, 0, 10, 2, 'x', k => k === 4 || k === 5);
    out.push({ kind: 'gateWide', at: 'edgeX', i: 4, j: 0, turn: 2 });
    run(out, 'sandbags', 0, 8, 10, 0, 'x');
    sandbagCorners(out, Array.from({ length: 11 }, (_, i) => [i, 8] as const));   // (every node of the parapet: in line, and its ends against the Hesco and the T-wall)
    run(out, 'hesco', 0, 0, 8, 3, 'z');
    run(out, 'tWall', 10, 0, 8, 1, 'z');
    run(out, 'wire', 0, -1, 10, 2, 'x', k => k === 4 || k === 5);
    box(out, { i0: 3, j0: 4, cols: 4, rows: 3, wall: doorAndWindows({ S: 1 }) });   // (cells 3–6: clear of the towers' cells either side, whose platforms reach past their cell)
    out.push({ kind: 'containerRoom', at: 'cell', i: 8, j: 1, turn: 3 });   // (its run north: cells (8, 1)–(8, 2) — turn 3 runs +z; turn 1 ran it south into the T-wall)
    crates(out, 6, 1); crates(out, 7, 1);
    tower(out, 1, 6);   // (a cell in from the walls and the parapet's node line, their stairs south of them)
    tower(out, 8, 6);
  }),
  wallLine: make('wallLine', 10, 1, 1, out => {
    run(out, 'tWall', 0, 0, 10, 2, 'x', k => k === 4);
    out.push({ kind: 'gate', at: 'edgeX', i: 4, j: 0, turn: 2 });
    run(out, 'wire', 0, -1, 10, 2, 'x', k => k === 4);
  }),
  gunPit: make('gunPit', 3, 3, 1, out => {
    run(out, 'sandbags', 0, 0, 3, 2, 'x');
    run(out, 'sandbags', 0, 0, 3, 3, 'z');
    run(out, 'sandbags', 3, 0, 3, 1, 'z');
    sandbagCorners(out, [[0, 0], [1, 0], [2, 0], [3, 0], [0, 1], [0, 2], [3, 1], [3, 2]]);   // (the front's four nodes and the sides' joints; the rear ends are free)
    crates(out, 1, 2);   // (the rear middle: a crate in a side cell met the corner pier at the node)
  }),
  motorPool: make('motorPool', 17, 15, 1, out => {
    run(out, 'hesco', 0, 0, 17, 2, 'x', k => k === 7 || k === 8);
    out.push({ kind: 'gateWide', at: 'edgeX', i: 7, j: 0, turn: 2 });   // (onto the passage through the rows, cells 7–9)
    run(out, 'hesco', 0, 15, 17, 0, 'x', k => k === 7 || k === 8);
    out.push({ kind: 'gateWide', at: 'edgeX', i: 7, j: 15, turn: 0 });   // (69.10: the back gate, onto the lighters' strip behind the pool)
    run(out, 'hesco', 0, 0, 15, 3, 'z');
    run(out, 'hesco', 17, 0, 15, 1, 'z');
    run(out, 'wire', 0, -1, 17, 2, 'x', k => k === 7 || k === 8);
  }),
};
